import { useEffect, useState } from 'react';
import * as Yup from 'yup';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Stack, Alert, IconButton, InputAdornment, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// components
import Iconify from '@raas/shared/src/components/minimals/iconify';
import FormProvider, { RHFTextField } from '@raas/shared/src/components/minimals/hook-form';
// auth
import { useAuthContext } from '@raas/shared/src/auth/useAuthContext';
import { useMsal } from '@azure/msal-react';
import { useLocales } from '@raas/shared/src/locales';
import { useKeycloak } from '@react-keycloak/web';

// ----------------------------------------------------------------------

type FormValuesProps = {
  email: string;
  password: string;
  afterSubmit?: string;
};

type Props = {
  requiredPermission: string;
  clientId: string;
};

export default function AuthLoginForm({ requiredPermission, clientId }: Props) {
  const { login, loginWithAccessToken } = useAuthContext();
  const { translate } = useLocales();
  const { instance } = useMsal();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().required('Email is required').email('Email must be a valid email address'),
    password: Yup.string().required('Password is required'),
  });

  const defaultValues = { email: '', password: '' };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    try {
      await login(data.email, data.password, requiredPermission);
    } catch (error) {
      reset();
      setError('afterSubmit', {
        ...error,
        message: error.message || error,
      });
    }
  };

  const handleAzureADLogin = async () => {
    const request = {
      scopes: [`${clientId}/.default`],
      prompt: 'select_account',
      forceRefresh: true,
    };

    try {
      const response = await instance.acquireTokenPopup(request);
      const { accessToken } = response;
      loginWithAccessToken(accessToken, requiredPermission);
      console.debug('Azure AD login successful');
    } catch (error) {
      console.error('Azure AD login error:', error);
      setError('afterSubmit', {
        ...error,
        message: error.message || error,
      });
      instance.clearCache();
    }
  };

  const { keycloak } = useKeycloak();
  const { authenticated } = keycloak;

  const handleKeycloakLogin = async () => {
    try {
      await keycloak.login();
    } catch (error) {
      console.error('Keycloak login error:', error);
      setError('afterSubmit', {
        ...error,
        message: error.message || error,
      });
    }
  };

  useEffect(() => {
    if (authenticated) {
      loginWithAccessToken(keycloak.token, requiredPermission);
    }
  }, [authenticated, loginWithAccessToken, keycloak.token, requiredPermission]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}

        <RHFTextField name="email" label="Email address" />

        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <LoadingButton
        fullWidth
        color="inherit"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitSuccessful || isSubmitting}
        sx={{
          my: 2,
          bgcolor: 'text.primary',
          color: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'grey.800'),
          '&:hover': {
            bgcolor: 'text.primary',
            color: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'grey.800'),
          },
        }}
      >
        Login
      </LoadingButton>
      <Button
        fullWidth
        color="inherit"
        variant="contained"
        onClick={handleAzureADLogin}
        sx={{
          bgcolor: 'text.primary',
          color: (theme: { palette: { mode: string } }) =>
            theme.palette.mode === 'light' ? 'common.white' : 'grey.800',
          '&:hover': {
            bgcolor: 'text.primary',
            color: (theme: { palette: { mode: string } }) =>
              theme.palette.mode === 'light' ? 'common.white' : 'grey.800',
          },
        }}
      >
        {translate('common.azureLoginButton')}
      </Button>
      {requiredPermission === 'INTERNAL_ACCESS' && (
        <Button
          fullWidth
          color="inherit"
          variant="contained"
          onClick={handleKeycloakLogin}
          sx={{
            my: 2,
            bgcolor: 'text.primary',
            color: (theme: { palette: { mode: string } }) =>
              theme.palette.mode === 'light' ? 'common.white' : 'grey.800',
            '&:hover': {
              bgcolor: 'text.primary',
              color: (theme: { palette: { mode: string } }) =>
                theme.palette.mode === 'light' ? 'common.white' : 'grey.800',
            },
          }}
        >
          {translate('common.keycloakLoginButton')}
        </Button>
      )}
    </FormProvider>
  );
}
